import {
  Component,
  OnInit,
  Injector,
  input,
  model,
  effect,
  inject,
  ChangeDetectionStrategy,
  viewChild,
  output,
} from '@angular/core';

import dayjs from 'dayjs';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  NgModel,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  FormsModule,
} from '@angular/forms';
import { Popover } from 'primeng/popover';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Bind } from 'primeng/bind';

dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear); // dependency for isoWeeksInYear

type weekString = `${number}-W${number}` | `W${number}`;
interface PickableItem {
  disabled: boolean;
  value: number;
}

interface DisplayPickableItem {
  label: string;
}

/**
 * Week picker (single or range via two instances). Accepts and exposes a week string (e.g. 2025-W12).
 *
 * @deprecated Use `ag1-week-range` instead. It has better form handling (reactive/template-driven),
 * better UX, and supports blank values. Filters and other consumers will be migrated over time.
 */
@Component({
  selector: 'ag1-week-picker',
  templateUrl: './ag1-week-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: Ag1WeekPickerComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: Ag1WeekPickerComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgSelectComponent, FormsModule, Bind, Popover],
})
export class Ag1WeekPickerComponent
  implements OnInit, ControlValueAccessor, Validator
{
  private injector = inject<Injector>(Injector);

  // input signals
  readonly showYears = input(true);
  readonly minWeek = input<weekString>(undefined);
  readonly maxWeek = input<weekString>(undefined);
  readonly middleYear = input<number>(dayjs().year());
  readonly displayMultiSelect = input<boolean>(false);

  readonly onWeekChange = output<weekString>();

  readonly ngModel = model<weekString>(undefined);

  private _backingDate: dayjs.Dayjs;
  public get backingDate(): dayjs.Dayjs {
    return this._backingDate;
  }
  public set backingDate(value: dayjs.Dayjs) {
    this._backingDate = value;
    const next: weekString = (
      this.showYears()
        ? this._backingDate.format('GGGG[-W]WW')
        : this._backingDate.format('[W]WW')
    ) as weekString;

    if (
      this.weeks.length !== 0 &&
      this.selectedYear != this._backingDate.year()
    ) {
      this.populateWeeks();
    }

    this.selectedYear = this._backingDate.isoWeekYear();
    this.selectedWeek = this._backingDate.isoWeek();

    this.displayWeek = this.selectedWeek;
    this.displayYear = this.selectedYear;

    this.updateUI();

    this.ngModel.set(next);
    this.onWeekChange.emit(next);
  }

  selectedWeek: number;
  selectedYear: number;

  displayWeek: number;
  displayYear: number;

  touched = false;
  disabled = false;

  weeks: PickableItem[] = [];
  displayWeeks: DisplayPickableItem[] = [];
  years: PickableItem[] = [];

  private minDate: dayjs.Dayjs;
  private maxDate: dayjs.Dayjs;
  lastWeekDisabled = true;
  thisWeekDisabled = true;
  nextWeekDisabled = true;

  // used to generate random ids for weeks and years
  // this was needed to get scrollIntoView functionality working
  // with multiple instance of this component open at the same time
  uniqueID: number;
  public control!: UntypedFormControl;

  readonly weekPickerPopover = viewChild.required<Popover>('weekPickerPopover');

  constructor() {
    this.uniqueID = Math.random();

    effect(() => {
      const value = this.ngModel();

      if (value) {
        this.backingDate = this.weekToDayJs(value);
      }
    });
  }

  onWeekPickerTriggerClick(event: Event): void {
    this.updateUI();
    this.weekPickerPopover().toggle(event);
  }

  closeWeekpicker() {
    if (this.displayMultiSelect() === false) {
      this.weekPickerPopover().hide();
    }
  }

  weekToDayJs(week: weekString) {
    // accept either a string like 2022-W30 or just W30
    const match = week?.match(/(?:(\d{4})-)?W(\d{1,2})/);

    if (match) {
      const [_entireMatch, , week] = match;
      let year = match[1];

      const showYears: boolean = this.showYears();
      if (showYears && !year) {
        throw new Error('no year provided');
      }

      if (!year && !showYears) {
        year = this.middleYear().toString();
      }

      return dayjs()
        .year(Number(year))
        .month(5) // for edge case near start/end of year
        .isoWeekday(1)
        .isoWeek(Number(week))
        .startOf('date');
    }

    throw new Error('invalid week');
  }

  ngOnInit() {
    this.setComponentControl();

    const minWeek: weekString = this.minWeek();
    if (minWeek) {
      this.minDate = this.weekToDayJs(minWeek);
    }

    const maxWeek: weekString = this.maxWeek();
    if (maxWeek) {
      this.maxDate = this.weekToDayJs(maxWeek);
    }

    if (this.ngModel()) {
      this.backingDate = this.weekToDayJs(this.ngModel());
    } else if (this.control.value) {
      this.backingDate = this.weekToDayJs(this.control.value);
    } else {
      this.backingDate = dayjs();
    }

    this.verifyButtonStates();

    this.populateYears();
    this.populateWeeks();
  }

  private setComponentControl(): void {
    const injectedControl = this.injector.get(NgControl);

    switch (injectedControl.constructor) {
      case NgModel: {
        const { control, update } = injectedControl as NgModel;

        this.control = control;
        break;
      }
      case FormControlName: {
        this.control = this.injector
          .get(FormGroupDirective)
          .getControl(injectedControl as FormControlName);
        break;
      }
      default: {
        this.control = (injectedControl as FormControlDirective)
          .form as UntypedFormControl;
        break;
      }
    }
  }

  currentWeekDate() {
    return dayjs(new Date()).day(1).startOf('date');
  }

  verifyButtonStates() {
    const date = this.currentWeekDate();

    this.lastWeekDisabled = !this.isDateValid(date.subtract(1, 'week'));
    this.thisWeekDisabled = !this.isDateValid(date);
    this.nextWeekDisabled = !this.isDateValid(date.add(1, 'week'));
  }

  isDateValid(date: dayjs.Dayjs) {
    let isValid = true;
    // date is > min && < max
    if (this.minDate && date < this.minDate) isValid = false;
    else if (this.maxDate && date > this.maxDate) isValid = false;

    return isValid;
  }

  populateYears() {
    this.years = [];
    // pad years with 5 years on either side of current year
    const padding = 5;
    const firstYear = this.middleYear() - padding;
    const lastYear = this.middleYear() + padding;

    const minYear = this.minDate?.year() || 0;
    const maxYear = this.maxDate?.year() || Number.MAX_VALUE;

    for (let y = firstYear; y < lastYear; y++) {
      const disabled = y < minYear || y > maxYear;

      this.years.push({ disabled: disabled, value: y });
    }
  }

  populateWeeks() {
    this.weeks = [];
    const isoWeeksInYear = this.backingDate.isoWeeksInYear();
    for (let week = 1; week <= isoWeeksInYear; week++) {
      const date = this.weekToDayJs(`${this.selectedYear}-W${week}`);

      const disabled = !this.isDateValid(date);

      this.weeks.push({
        disabled: disabled,
        value: week,
      });
    }

    this.populateDisplayWeeks();
  }

  updateUI() {
    this.scrollIntoView(this.selectedYear);
    this.scrollIntoView(this.selectedWeek);
  }

  setWeek(week) {
    this.backingDate = dayjs(this.backingDate).isoWeek(week);
    this.closeWeekpicker();
  }

  setYear(year) {
    const newYear = dayjs().year(year);
    if (this.selectedWeek > newYear.isoWeeksInYear()) {
      this.selectedWeek = newYear.isoWeeksInYear();
    }
    this.backingDate = this.weekToDayJs(`${year}-W${this.selectedWeek}`);
  }

  previousWeek() {
    this.backingDate = this.currentWeekDate().subtract(1, 'week');
    this.closeWeekpicker();
  }

  thisWeek() {
    this.backingDate = this.currentWeekDate();
    this.closeWeekpicker();
  }

  nextWeek() {
    this.backingDate = this.currentWeekDate().add(1, 'week');
    this.closeWeekpicker();
  }

  async scrollIntoView(id: number, scrolling?: 'auto') {
    const uniqueId = `${this.uniqueID}-${id}`;
    while (!document.getElementById(uniqueId)) {
      await new Promise((r) => setTimeout(r, 50));
    }
    document
      .getElementById(uniqueId)
      .scrollIntoView({ behavior: scrolling, block: 'center' });
  }

  onChange = (ngModel) => {};

  onTouched = () => {};

  writeValue(ngModel: string) {
    //this.ngModel = ngModel;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  onSelection() {
    // tell parent custom form element was interacted with
    this.markAsTouched();

    if (!this.disabled) {
      // tell parent data changed
      // this.ngModel = this.selectedWeek;
      this.onChange(this.ngModel());
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ngModel = control.value;
    if (ngModel) {
      const [yearPart, weekPart] = ngModel
        .replace('W')
        .split('-')
        .map((part) => parseInt(part, 10));

      // check if week is valid
      const totalWeeks = dayjs().year(yearPart).isoWeeksInYear();
      if (weekPart > totalWeeks) {
        return {
          mustBeAValidWeek: {
            ngModel,
          },
        };
      }
    }
  }

  populateDisplayWeeks() {
    const displayWeeks = this.weeks.map((week) => {
      return { ...week, label: `Week ${week.value}` } as DisplayPickableItem;
    });

    this.displayWeeks = displayWeeks;
  }
}
